const EXCEL_COLOR_ERROR_SENTINEL = 'Values must be of type <class \'str\'>';

// Workbook theme indexes only reach the client when the backend cannot resolve them to RGB.
const EXCEL_THEME_FALLBACK_COLORS: Record<number, string> = {
  0: '#000000',
  1: '#FFFFFF',
  2: '#44546A',
  3: '#E7E6E6',
  4: '#BFCDCE',
  5: '#F0F1F1',
  6: '#EEDDDB',
  7: '#F6EDE5',
  8: '#218EF5',
  9: '#89C1FF',
  10: '#0563C1',
  11: '#954F72',
};

const EXCEL_INDEXED_COLORS: Record<number, string> = {
  0: '#000000',
  1: '#FFFFFF',
  2: '#FF0000',
  3: '#00FF00',
  4: '#0000FF',
  5: '#FFFF00',
  6: '#FF00FF',
  7: '#00FFFF',
  8: '#000000',
  9: '#FFFFFF',
  10: '#FF0000',
  11: '#00FF00',
  12: '#0000FF',
  13: '#FFFF00',
  14: '#FF00FF',
  15: '#00FFFF',
  16: '#800000',
  17: '#008000',
  18: '#000080',
  19: '#808000',
  20: '#800080',
  21: '#008080',
  22: '#C0C0C0',
  23: '#808080',
  24: '#9999FF',
  25: '#993366',
  26: '#FFFFCC',
  27: '#CCFFFF',
  28: '#660066',
  29: '#FF8080',
  30: '#0066CC',
  31: '#CCCCFF',
  32: '#000080',
  33: '#FF00FF',
  34: '#FFFF00',
  35: '#00FFFF',
  36: '#800080',
  37: '#800000',
  38: '#008080',
  39: '#0000FF',
  40: '#00CCFF',
  41: '#CCFFFF',
  42: '#CCFFCC',
  43: '#FFFF99',
  44: '#99CCFF',
  45: '#FF99CC',
  46: '#CC99FF',
  47: '#FFCC99',
  48: '#3366FF',
  49: '#33CCCC',
  50: '#99CC00',
  51: '#FFCC00',
  52: '#FF9900',
  53: '#FF6600',
  54: '#666699',
  55: '#969696',
  56: '#003366',
  57: '#339966',
  58: '#003300',
  59: '#003333',
  60: '#003399',
  61: '#663300',
  62: '#993300',
  63: '#993366',
};

const getReferencedExcelColor = (value: string, prefix: 'theme' | 'indexed'): string | undefined => {
  const normalizedValue = value.toLowerCase();

  if (!normalizedValue.startsWith(`${prefix}:`)) {
    return undefined;
  }

  const colorIndex = Number(normalizedValue.replace(`${prefix}:`, ''));
  if (!Number.isInteger(colorIndex)) {
    return undefined;
  }

  if (prefix === 'theme') {
    return EXCEL_THEME_FALLBACK_COLORS[colorIndex];
  }

  return colorIndex >= 64 ? undefined : EXCEL_INDEXED_COLORS[colorIndex];
};

export const parseExcelColorToCss = (color: unknown): string | undefined => {
  if (typeof color !== 'string' || !color.trim() || color.includes(EXCEL_COLOR_ERROR_SENTINEL)) {
    return undefined;
  }

  const normalizedColor = color.trim();

  if (normalizedColor === 'auto') {
    return undefined;
  }

  const referencedColor =
    getReferencedExcelColor(normalizedColor, 'theme') ?? getReferencedExcelColor(normalizedColor, 'indexed');
  if (referencedColor) {
    return referencedColor;
  }

  if (/^(rgb|hsl)a?\(/i.test(normalizedColor) || /^[a-z]+$/i.test(normalizedColor)) {
    return normalizedColor;
  }

  const hex = normalizedColor.replace(/^#/, '').toUpperCase();

  if (/^[0-9A-F]{8}$/.test(hex)) {
    const alphaByte = parseInt(hex.substring(0, 2), 16);
    if (alphaByte === 0) {
      return undefined;
    }

    const red = parseInt(hex.substring(2, 4), 16);
    const green = parseInt(hex.substring(4, 6), 16);
    const blue = parseInt(hex.substring(6, 8), 16);
    const alpha = alphaByte / 255;

    if (alphaByte === 255) {
      return `#${hex.substring(2)}`;
    }

    return `rgba(${red}, ${green}, ${blue}, ${Number.isFinite(alpha) ? alpha.toFixed(3) : 1})`;
  }

  if (/^[0-9A-F]{6}$/.test(hex)) {
    return `#${hex}`;
  }

  if (/^[0-9A-F]{3}$/.test(hex)) {
    const [red, green, blue] = hex;

    return `#${red}${red}${green}${green}${blue}${blue}`;
  }

  return undefined;
};
